import { GAME_ASSET_SLUG } from "@/config/game";
import { repeatable } from "@/icons";
import {
  Evolution,
  EvolutionCategory,
  EvolutionSource,
  EvolutionState,
  PlayStyleUpgrade,
} from "@/redux/evolutions/evolutionsSlice";
import {
  contentLabelsSelector,
  eventTokensSelector,
} from "@/redux/loadedContent/loadedContentSlice";
import { BREAKPOINTS } from "@/settings";
import { SOURCE_ICON_PATHS, SOURCE_LABELS } from "@repo/shared/evolutions";
import {
  type TranslateFn,
  formatDate,
  useTranslation,
} from "@repo/shared/i18n";
import type { EventToken, EvoRowValues } from "@repo/shared/types";
import { Dna, HelpCircle } from "lucide-react";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import { useMediaQuery } from "usehooks-ts";

import {
  evoStateItems,
  getEvoStateIcon,
} from "../../EvolutionsOverviewPage/EvolutionsOverviewPage";

/**
 * Localized label for a requirement or upgrade row. `key` is either a
 * canonical attribute key (the `playerAttributes` labels dict) or one of the
 * `labels.*` slugs; the backend's English label is the fallback.
 */
export const useEvoRowLabel = () => {
  const { t } = useTranslation("evolutions");
  const { playerAttributes } = useSelector(contentLabelsSelector);
  return ({ key, label }: { key: string; label: string }): string =>
    playerAttributes[key]?.label ??
    t(`labels.${key}` as never, { defaultValue: label });
};

/**
 * Localized value for a requirement or upgrade row, composed from the
 * structured fields (ADR 0024). Thresholds carry `operator` + `amount`,
 * bonuses `amount` (+ `cap`), birthdays `operator` + `date`, joined lists
 * `values`. A row without any of them is from an older backend; its English
 * `value` is shown as-is.
 */
export const useEvoRowValue = () => {
  const { t, i18n } = useTranslation("evolutions");
  return (row: EvoRowValues & { value: string }): string => {
    if (row.values) {
      return new Intl.ListFormat(i18n.language || "en-US", {
        type: "disjunction",
      }).format(row.values);
    }
    if (row.date) {
      const date = formatDate(row.date, {
        year: "numeric",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      });
      if (row.operator === "min") return t("values.bornAfter", { date });
      if (row.operator === "max") return t("values.bornBefore", { date });
      return date;
    }
    if (row.amount != null) {
      const { amount, cap } = row;
      if (row.operator === "min") return t("values.min", { amount });
      if (row.operator === "max") return t("values.max", { amount });
      if (row.operator === "exact") return String(amount);
      return cap != null
        ? t("values.bonusCapped", { amount, cap })
        : t("values.bonus", { amount });
    }
    return String(row.value);
  };
};

const cdnBase = import.meta.env.VITE_APP_ASSETS_BASE_URL;

// Paths and labels come from shared so the app and the site cannot drift.
const sourceIconMap: Record<string, { src: string; alt: string }> =
  Object.fromEntries(
    Object.entries(SOURCE_ICON_PATHS).map(([type, path]) => [
      type,
      { src: `${cdnBase}${path}`, alt: SOURCE_LABELS[type] },
    ]),
  );

const sourceLabelMap = SOURCE_LABELS;

export const getSourceDisplay = (
  source: EvolutionSource | undefined,
  state: EvolutionState,
  t: TranslateFn<"evolutions">,
  eventTokens: EventToken[],
) => {
  if (!source) {
    if (state === "unachieved") {
      return {
        sourceIcon: <HelpCircle size={16} />,
        sourceLabel: t("info.unknownSource"),
        sourceValue: undefined,
      };
    }
    return { sourceIcon: null, sourceLabel: undefined, sourceValue: undefined };
  }

  const iconInfo = sourceIconMap[source.type];
  const icon = iconInfo ? (
    <img src={iconInfo.src} alt={iconInfo.alt} className="size-4" />
  ) : (
    <HelpCircle size={16} />
  );

  if (source.type === "token-store") {
    const token = eventTokens.find(
      (eventToken) => eventToken.eventTokenId === source.eventTokenId,
    );
    return {
      sourceIcon: token ? (
        <img
          src={`${cdnBase}/${token.iconKey}`}
          alt={token.displayName}
          className="size-4"
        />
      ) : (
        icon
      ),
      sourceLabel: t("info.tokenStore"),
      sourceValue:
        source.cost != null
          ? token
            ? t("values.tokenStoreCost", {
                cost: source.cost,
                token: token.displayName,
              })
            : t("values.tokenStoreCostUnknownToken", { cost: source.cost })
          : undefined,
    };
  }

  if (source.type === "season-pass") {
    return {
      sourceIcon: icon,
      sourceLabel: t("info.seasonPass"),
      sourceValue:
        source.level != null
          ? t(
              source.premium
                ? "values.seasonPassPremium"
                : "values.seasonPassStandard",
              { level: source.level },
            )
          : source.name,
    };
  }

  const value = source.subname
    ? `${source.name}: ${source.subname}`
    : source.name;
  const label = sourceLabelMap[source.type] ?? source.name;

  return { sourceIcon: icon, sourceLabel: label, sourceValue: value };
};

export const getMatchesDisplay = (matches: number | undefined) => {
  if (matches == null) {
    return { matchesLabel: "?", matchesColor: "text-dark-tertiary" };
  }
  let color = "text-emerald-500";
  if (matches >= 6) {
    color = "text-red-500";
  } else if (matches >= 3) {
    color = "text-yellow-500";
  }
  return { matchesLabel: String(matches), matchesColor: color };
};

type RowComponent = React.ComponentType<{
  label: React.ReactNode;
  value: React.ReactNode;
  icon?: React.ReactNode;
}>;

export const EvoInfoRows = ({
  evolution,
  category,
  Row,
}: {
  evolution: Evolution;
  category?: EvolutionCategory;
  Row: RowComponent;
}) => {
  const { t } = useTranslation("evolutions");
  const eventTokens = useSelector(eventTokensSelector);
  const evoState = evoStateItems.find((item) => item.state === evolution.state);
  const { sourceIcon, sourceLabel, sourceValue } = useMemo(
    () => getSourceDisplay(evolution.source, evolution.state, t, eventTokens),
    [evolution.source, evolution.state, t, eventTokens],
  );
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  const { matchesLabel } = useMemo(
    () => getMatchesDisplay(evolution.matches),
    [evolution.matches],
  );

  const numberOfRows = useMemo(() => {
    let count = 0;
    if (matchesLabel) count++;
    if (evolution.repeats > 0) count++;
    if (sourceLabel) count++;
    return count;
  }, [category, matchesLabel, evolution.repeats, sourceLabel]);

  return (
    <>
      <Row
        icon={getEvoStateIcon(evolution.state, 16)}
        label={t("info.state")}
        value={evoState ? t(evoState.labelKey) : ""}
      />
      <Row
        icon={<Dna size={16} />}
        label={t("info.category")}
        value={category.name}
      />
      <Row
        icon={
          <img
            src={`${cdnBase}/misc/ea/ball.png`}
            alt={t("alt.matches")}
            className="size-4"
          />
        }
        label={t("info.matches")}
        value={
          <span className="font-medium text-dark-secondary">
            {matchesLabel}
          </span>
        }
      />
      {evolution.repeats > 0 && (
        <Row
          icon={
            <div className="size-4">
              <div className="size-3.5">{repeatable}</div>
            </div>
          }
          label={t("info.repeats")}
          value={
            <span className="font-medium text-dark-secondary">
              {evolution.repeats}
            </span>
          }
        />
      )}
      {sourceLabel && (
        <Row
          icon={<div className="size-4">{sourceIcon}</div>}
          label={sourceLabel}
          value={sourceValue}
        />
      )}
      {numberOfRows < 2 && !isTouchDevice && <div className="h-5"></div>}
    </>
  );
};

export const PlayStyleIcon = ({ upgrade }: { upgrade: PlayStyleUpgrade }) => {
  const folder = upgrade.isPlus ? "playstylesplus" : "playstyles";
  return (
    <img
      src={`${cdnBase}/${GAME_ASSET_SLUG}/${folder}/${upgrade.id}.png`}
      alt={upgrade.value}
      className="size-7"
    />
  );
};
